/**
 * Zicasso Dashboard — clon del panel de Zicasso Partners para gestionar
 * leads sin salir de SOFI. Datos vienen de `/api/zicasso/*` (backend
 * sincronizado periódicamente vía Playwright).
 */
import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import axios from "axios";
import { AutomationStoppedPill } from "@/components/AutomationStoppedPill";
import {
  RefreshCw, Clock, Mail, AlertTriangle, Users, Compass, Search,
  Phone, Calendar, MessageSquare, MapPin, DollarSign, ChevronRight,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ZicassoLeadModal from "./ZicassoLeadModal";
import ZicassoBestPractices from "./ZicassoBestPractices";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sofi_token") || ""}` });

// Estados oficiales de Zicasso (Sales Stage) que aparecen en el partner portal
const STAGES = [
  { key: "", label: "Nuevo", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "FIRST_MESSAGE_SENT", label: "1er mensaje", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "CONSULTATION", label: "Consultation", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "ITINERARY_IN_REVIEW", label: "Itinerario en revisión", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { key: "ITINERARY_SENT", label: "Itinerario enviado", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "PROPOSAL_SENT", label: "Propuesta enviada", color: "bg-teal-50 text-teal-700 border-teal-200" },
];

const initialsFrom = (name = "") => name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const relativeDate = (iso) => {
  if (!iso || iso.startsWith("0001")) return "—";
  try {
    const d = new Date(iso);
    return format(d, "d MMM yyyy", { locale: es });
  } catch { return "—"; }
};

const daysUntil = (iso) => {
  if (!iso || iso.startsWith("0001")) return null;
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
};

function StatCard({ icon: Icon, label, value, tint = "text-primary", testid }) {
  return (
    <Card className="border-l-4" data-testid={testid} style={{ borderLeftColor: "currentColor" }}>
      <CardContent className={`flex items-center gap-3 p-4 ${tint}`}>
        <Icon className="h-6 w-6" />
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function LeadCard({ lead, onOpen }) {
  const stage = STAGES.find((s) => s.key === (lead.sales_stage || "")) || STAGES[0];
  const dueDays = daysUntil(lead.reminder_due_at);
  const dueLabel = dueDays === null
    ? "Sin recordatorio"
    : dueDays < 0
    ? `Atrasado ${Math.abs(dueDays)}d`
    : dueDays === 0
    ? "Hoy"
    : `En ${dueDays}d`;

  return (
    <Card
      className={`cursor-pointer transition-colors hover:bg-accent/50 ${lead.is_overdue ? "border-red-300" : ""}`}
      onClick={() => onOpen(lead.id)}
      data-testid={`zicasso-lead-card-${lead.id}`}
    >
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="truncate text-sm font-bold" data-testid={`lead-traveler-name-${lead.id}`}>
                {lead.traveler_name || "—"}
              </div>
              {lead.repeat_traveler && (
                <Badge variant="outline" className="h-4 border-purple-300 bg-purple-50 px-1 text-[9px] font-medium text-purple-700">
                  Repeat
                </Badge>
              )}
              {lead.has_unread_from_traveler && (
                <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" title="Mensaje sin leer del cliente" />
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{(lead.countries || []).join(", ")} · {lead.total_nights}n · {lead.adults}A {lead.children > 0 ? `${lead.children}N` : ""}</span>
            </div>
          </div>
          <Badge className={`h-5 shrink-0 border ${stage.color}`}>
            {stage.label}
          </Badge>
        </div>

        <div className="flex items-center justify-between gap-2 border-t pt-2">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Avatar className="h-5 w-5">
              <AvatarFallback className="bg-slate-200 text-[9px] text-slate-700">{initialsFrom(lead.assigned_zc_agent_name)}</AvatarFallback>
            </Avatar>
            <span className="truncate max-w-[110px]">{lead.assigned_zc_agent_name}</span>
          </div>
          <div className={`flex shrink-0 items-center gap-1 text-[11px] font-medium ${lead.is_overdue ? "text-red-600" : "text-muted-foreground"}`}>
            <Clock className="h-3 w-3" />
            {dueLabel}
          </div>
        </div>

        {lead.budget_usd > 0 && (
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground">
              <Calendar className="mr-1 inline h-3 w-3" /> {relativeDate(lead.departure_date)}
            </span>
            <span className="font-medium text-emerald-700">
              <DollarSign className="mr-0.5 inline h-3 w-3" />{(lead.budget_usd || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="truncate text-sm">{value}</div>
      </div>
    </div>
  );
}

export default function ZicassoDashboard() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({
    sales_stage: "__all__",
    assigned_zc_agent_id: "__all__",
    overdue_only: false,
    unread_only: false,
    q: "",
  });
  const [selectedLead, setSelectedLead] = useState(null);

  const stats = useQuery({
    queryKey: ["zicasso-stats"],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/stats`, { headers: authHeaders() })).data,
    refetchInterval: 30_000,
  });

  const agents = useQuery({
    queryKey: ["zicasso-agents"],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/agents`, { headers: authHeaders() })).data,
  });

  const params = useMemo(() => {
    const p = new URLSearchParams({ status: "gestion", limit: "500" });
    if (filters.sales_stage !== "__all__") {
      // "__new" representa el stage vacío ("") de Zicasso (lead recién aceptado)
      p.set("sales_stage", filters.sales_stage === "__new" ? "" : filters.sales_stage);
    }
    if (filters.assigned_zc_agent_id !== "__all__") p.set("assigned_zc_agent_id", filters.assigned_zc_agent_id);
    if (filters.overdue_only) p.set("overdue_only", "true");
    if (filters.unread_only) p.set("unread_only", "true");
    if (filters.q) p.set("q", filters.q);
    return p.toString();
  }, [filters]);

  const leads = useQuery({
    queryKey: ["zicasso-leads", params],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/leads?${params}`, { headers: authHeaders() })).data,
    keepPreviousData: true,
  });

  const syncMut = useMutation({
    mutationFn: () => axios.post(`${API}/api/zicasso/sync?fetch_messages=true`, {}, { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Sincronización arrancada en background");
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["zicasso-stats"] });
        qc.invalidateQueries({ queryKey: ["zicasso-leads"] });
      }, 5000);
    },
    onError: () => toast.error("Error al arrancar el sync"),
  });

  const groupedByStage = useMemo(() => {
    const items = leads.data?.items || [];
    const map = {};
    STAGES.forEach((s) => (map[s.key] = []));
    items.forEach((l) => {
      const key = l.sales_stage || "";
      if (!map[key]) map[key] = [];
      map[key].push(l);
    });
    return map;
  }, [leads.data]);

  return (
    <div className="mx-auto max-w-[1600px] p-4" data-testid="zicasso-dashboard">
      {/* Top-level tabs: Leads Kanban vs Best Practices */}
      <Tabs defaultValue="leads" className="w-full">
        <TabsList className="mb-4" data-testid="zicasso-top-tabs">
          <TabsTrigger value="leads" data-testid="zicasso-tab-leads">
            <Users className="mr-1 h-3 w-3" />
            Leads {stats.data?.totals?.active !== undefined && `(${stats.data.totals.active})`}
          </TabsTrigger>
          <TabsTrigger value="best-practices" data-testid="zicasso-tab-bestpractices">
            <Compass className="mr-1 h-3 w-3" />
            Reglas & Automatizaciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-0">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Compass className="h-6 w-6 text-primary" />
            Zicasso
          </h1>
          <p className="text-xs text-muted-foreground">
            {stats.data?.last_sync
              ? `Última sync: ${format(new Date(stats.data.last_sync.ts), "d MMM HH:mm", { locale: es })} · ${stats.data.last_sync.messages_new} mensajes nuevos`
              : "Sin sincronizar aún"}
          </p>
          <AutomationStoppedPill className="mt-1" testid="zicasso-automation-stopped" />
        </div>
        <Button
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
          data-testid="sync-now-button"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${syncMut.isPending ? "animate-spin" : ""}`} />
          Sincronizar ahora
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard icon={Users} label="Leads Activos" value={stats.data?.totals?.active ?? "—"} tint="text-blue-600" testid="stat-active" />
        <StatCard icon={AlertTriangle} label="Atrasados" value={stats.data?.totals?.overdue ?? "—"} tint="text-red-600" testid="stat-overdue" />
        <StatCard icon={Mail} label="Sin leer" value={stats.data?.totals?.unread_from_traveler ?? "—"} tint="text-amber-600" testid="stat-unread" />
        <StatCard icon={Compass} label="Estados" value={stats.data?.by_stage?.length ?? "—"} tint="text-emerald-600" testid="stat-stages" />
      </div>

      {/* Filtros */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar cliente, título, ref..."
            className="pl-8"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="zicasso-filter-search"
          />
        </div>
        <Select value={filters.assigned_zc_agent_id} onValueChange={(v) => setFilters({ ...filters, assigned_zc_agent_id: v })}>
          <SelectTrigger className="w-[180px]" data-testid="zicasso-filter-agent">
            <SelectValue placeholder="Todos los agentes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los agentes</SelectItem>
            {(agents.data || []).map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.sales_stage} onValueChange={(v) => setFilters({ ...filters, sales_stage: v })}>
          <SelectTrigger className="w-[180px]" data-testid="zicasso-filter-stage">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los estados</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.key || "__new"} value={s.key || "__new"}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant={filters.overdue_only ? "default" : "outline"}
          size="sm"
          onClick={() => setFilters({ ...filters, overdue_only: !filters.overdue_only })}
          data-testid="zicasso-filter-overdue"
        >
          <AlertTriangle className="mr-1 h-3 w-3" />
          Solo atrasados
        </Button>
        <Button
          variant={filters.unread_only ? "default" : "outline"}
          size="sm"
          onClick={() => setFilters({ ...filters, unread_only: !filters.unread_only })}
          data-testid="zicasso-filter-unread"
        >
          <Mail className="mr-1 h-3 w-3" />
          Sin leer
        </Button>
      </div>

      {/* Kanban / Grid */}
      {leads.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (leads.data?.items || []).length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="zicasso-empty-state">
          <p>Sin leads activos con estos filtros.</p>
        </Card>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="zicasso-kanban">
          {STAGES.map((stage) => {
            const items = groupedByStage[stage.key] || [];
            if (items.length === 0 && filters.sales_stage !== "__all__"
                && (filters.sales_stage === "__new" ? stage.key !== "" : filters.sales_stage !== stage.key)) return null;
            return (
              <div key={stage.key || "__new"} className="w-[320px] shrink-0" data-testid={`zicasso-column-${stage.key || "new"}`}>
                <div className={`mb-2 flex items-center justify-between rounded-md border px-2 py-1.5 ${stage.color}`}>
                  <span className="text-xs font-bold uppercase tracking-wider">{stage.label}</span>
                  <Badge variant="outline" className="h-5 bg-white/60 text-[10px]">{items.length}</Badge>
                </div>
                <div className="space-y-2">
                  {items.length === 0 ? (
                    <p className="rounded border border-dashed py-4 text-center text-[11px] text-muted-foreground">Sin leads</p>
                  ) : items.map((lead) => (
                    <LeadCard key={lead.id} lead={lead} onOpen={setSelectedLead} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal detalle centrado */}
      <ZicassoLeadModal
        leadId={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
      />
        </TabsContent>

        <TabsContent value="best-practices" className="mt-0">
          <ZicassoBestPractices />
        </TabsContent>
      </Tabs>
    </div>
  );
}
